import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { dataHora, brl } from "@/lib/format";
import {
  PageHeader,
  Panel,
  Th,
  Td,
  EmptyState,
  Escolha,
  Carregando,
  Erro,
} from "@/components/parker";
import { useAuditoria } from "@/lib/dados";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Download } from "lucide-react";
import { baixarCSV } from "@/lib/exportar";

export const Route = createFileRoute("/_authenticated/auditoria")({ component: Auditoria });

const ACOES: Record<string, string> = {
  insert: "criou",
  update: "alterou",
  delete: "apagou",
};

/**
 * Resume um documento do evento em uma linha legível. A trilha guarda o
 * documento inteiro; a tela mostra o que identifica a mudança.
 */
function resumo(j: Record<string, unknown> | null): string {
  if (!j) return "—";
  const partes: string[] = [];
  if (typeof j.descricao === "string") partes.push(j.descricao);
  if (typeof j.nome === "string") partes.push(j.nome);
  if (typeof j.valorCentavos === "number") partes.push(brl(j.valorCentavos));
  if (typeof j.status === "string") partes.push(j.status);
  if (Array.isArray(j.papeis)) partes.push((j.papeis as string[]).join(", "));
  return partes.length > 0 ? partes.join(" · ") : "—";
}

function Auditoria() {
  const eventos = useAuditoria(300);
  const [busca, setBusca] = useState("");
  const [entidade, setEntidade] = useState("");

  if (eventos.erro) return <Erro mensagem={eventos.erro} />;

  const entidades = [...new Set(eventos.dados.map((e) => e.entidade))].sort();
  const termo = busca.trim().toLowerCase();

  const linhas = eventos.dados.filter((e) => {
    if (entidade && e.entidade !== entidade) return false;
    if (!termo) return true;
    return (
      e.entidade.toLowerCase().includes(termo) ||
      (e.autorNome ?? "").toLowerCase().includes(termo) ||
      JSON.stringify(e.valorNovo ?? e.valorAnterior ?? "")
        .toLowerCase()
        .includes(termo)
    );
  });

  function exportar() {
    baixarCSV(
      "auditoria",
      ["Quando", "Autor", "Entidade", "Id", "Ação", "Origem", "Antes", "Depois"],
      linhas.map((e) => [
        dataHora(e.createdAt),
        e.autorNome ?? "sistema",
        e.entidade,
        e.entidadeId,
        ACOES[e.acao] ?? e.acao,
        e.origem,
        resumo(e.valorAnterior),
        resumo(e.valorNovo),
      ]),
    );
  }

  return (
    <>
      <PageHeader
        title="Auditoria"
        subtitle="Todo evento registrado com autor, data e hora, valor anterior e novo, e origem. Ninguém escreve aqui — nem administrador."
        actions={
          <Button variant="outline" onClick={exportar} disabled={linhas.length === 0}>
            <Download className="size-4" /> Exportar
          </Button>
        }
      />

      <div className="mb-6 flex flex-col gap-2 sm:flex-row sm:flex-wrap">
        <Input
          placeholder="Buscar por descrição, autor ou status"
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
          className="max-w-md"
        />
        <Escolha
          className="w-full sm:w-56"
          value={entidade}
          onChange={setEntidade}
          permitirVazio
          placeholder="Todas as entidades"
          options={entidades.map((e) => ({ id: e, label: e }))}
        />
      </div>

      <Panel className="overflow-x-auto">
        {eventos.carregando ? (
          <Carregando linhas={8} />
        ) : (
          <>
            <table className="w-full min-w-[1000px] text-sm">
              <thead>
                <tr>
                  <Th>Quando</Th>
                  <Th>Autor</Th>
                  <Th>Entidade</Th>
                  <Th>Ação</Th>
                  <Th>Origem</Th>
                  <Th>Antes</Th>
                  <Th>Depois</Th>
                </tr>
              </thead>
              <tbody>
                {linhas.map((e) => (
                  <tr key={e.id}>
                    <Td className="num whitespace-nowrap">{dataHora(e.createdAt)}</Td>
                    <Td>{e.autorNome ?? "sistema"}</Td>
                    <Td className="text-muted-foreground">{e.entidade}</Td>
                    <Td>{ACOES[e.acao] ?? e.acao}</Td>
                    <Td className="text-muted-foreground">{e.origem}</Td>
                    <Td className="max-w-[240px] truncate text-muted-foreground">
                      {resumo(e.valorAnterior)}
                    </Td>
                    <Td className="max-w-[240px] truncate">{resumo(e.valorNovo)}</Td>
                  </tr>
                ))}
              </tbody>
            </table>
            {linhas.length === 0 ? <EmptyState title="Nenhum evento registrado" /> : null}
          </>
        )}
      </Panel>
    </>
  );
}
